import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import queryString from 'query-string'
import {
  getCreateChatContextRequest,
  getDefaultChatContext,
} from 'src/components/Reusable/ChatContext/chatContext.utils'
import {
  ChatContext,
  useUpdateChatContext,
} from 'src/components/Reusable/ChatContext/useChatContext.hooks'
import { useAsyncInterval } from 'src/Utils/useAsyncInterval'
import { ChatServicePromiseClient } from '@trustero/trustero-api-web/lib/chat/chat_grpc_web_pb'
import { ClientMessageType } from '@trustero/trustero-api-web/lib/tichatapi/agent_pb'
import { useAuthorizedGrpcClient } from 'src/adapter'
import log from 'loglevel'
import {
  AppCopilotReferringContextMetadata as AppCopilotReferringContextMetadataProto,
  AnswerReactionType,
  Chat,
  ChatMessage,
  CreateAppCopilotChatRequest,
  CreateAppCopilotChatResponse,
  CreateChatRequest,
  DeleteChatRequest,
  DeleteReactionToAnswerRequest,
  GetChatMessagesSinceRequest,
  GetChatRequest,
  GetChatResponse,
  GetOrCreateAppCopilotChatRequest,
  GetIntermediateStatusResponse,
  ListAppCopilotChatsRequest,
  ListAppCopilotChatsResponse,
  ReactToAnswerRequest,
  ReferringContext as ReferringContextProto,
  SendChatMessageRequest,
  UpdateChatRequest,
} from '@trustero/trustero-api-web/lib/chat/chat_pb'
import { QUESTIONNAIRE_ANSWER_TYPE } from '@trustero/trustero-api-web/lib/questionnaire/questionnaire_pb'
import { NewGrpcResponse } from 'src/components/async/hooks/types'
import { GrpcResponse } from 'src/components/async/hooks/types'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { useSwrGrpc } from 'src/components/async/useSwrGrpc'
import { TaskManagerPromiseClient } from '@trustero/trustero-api-web/lib/task/task_grpc_web_pb'
import { TaskID } from '@trustero/trustero-api-web/lib/task/task_pb'
import { generatePermalink } from 'src/components/PageLayout/Permalink/generatePermalink'
import {
  FileUploadBody,
  MODEL_TYPE,
} from '@trustero/trustero-api-web/lib/common/model_pb'
import {
  ReferringContext,
  ReferringContextMetadata,
} from 'src/components/Copilot/AppCopilot/AppCopilot.types'
import { PlaybookInputs } from '@trustero/trustero-api-web/lib/playbook/playbook_pb'
import { TrusteroIntelligenceAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import { useAgentChat } from 'src/context/Gatekeeper'
import { getBytesPayload } from 'src/Utils/File/file'
import { useAccountBalanceValidator } from '../SecurityQuestionnaire/securityQuestionnaire.hooks'

export const DEFAULT_IS_PRIVATE = false

const toAppCopilotMetadataProto = (
  referringContextMetadata?: ReferringContextMetadata,
): AppCopilotReferringContextMetadataProto =>
  new AppCopilotReferringContextMetadataProto()
    .setFilterQuery(referringContextMetadata?.filterQuery || '')
    .setResultIdsList(referringContextMetadata?.resultIds || [])
    .setResultsCount(referringContextMetadata?.resultsCount || 0)

export type UseCreateChatResponse = {
  chat: Chat
}

export const useCreateChat = (): (() => Promise<UseCreateChatResponse>) => {
  const isAgentChat = useAgentChat()
  const client = useAuthorizedGrpcClient(ChatServicePromiseClient)

  return useCallback(async (): Promise<UseCreateChatResponse> => {
    const request = new CreateChatRequest()
    if (isAgentChat) {
      request.setIsAgentChat(isAgentChat)
    }
    request.setChatContext(getCreateChatContextRequest(getDefaultChatContext()))
    request.setIsPrivate(DEFAULT_IS_PRIVATE)
    const response = await client.createChat(request)

    const chat = response.getChat()

    if (chat) {
      return {
        chat,
      }
    }

    throw new Error('Failed to create chat')
  }, [client, isAgentChat])
}

export const useGetChat = (chatId: string): GrpcResponse<GetChatResponse> => {
  const shouldFetch = !!(chatId && chatId.length)
  const request = new GetChatRequest().setChatId(chatId || '')
  const { response } = useSwrImmutableGrpc(
    ChatServicePromiseClient.prototype.getChat,
    request,
    shouldFetch,
  )

  return NewGrpcResponse(response)
}

export type UseSendChatMessageProps = {
  chat: Chat
  prompt: string
  messageType?: ClientMessageType
  fileUploads?: File[]
  playbookInputs?: PlaybookInputs
  onSuccess: (questionId: string, taskId: string) => Promise<void>
  onError: (error: unknown) => void
}

export const useSendChatMessage = (): ((
  props: UseSendChatMessageProps,
) => Promise<void>) => {
  const isAgentChat = useAgentChat()
  const client = useAuthorizedGrpcClient(ChatServicePromiseClient)
  const validateAccountBalance = useAccountBalanceValidator({
    softLimitConfirmText: 'Continue with Trustero Intelligence',
  })

  const sendChatMessage = useCallback(
    async ({
      chat,
      prompt,
      onSuccess,
      onError,
      messageType,
      fileUploads,
      playbookInputs,
    }: UseSendChatMessageProps) => {
      const request = new SendChatMessageRequest()
        .setChatId(chat.getId())
        .setQuestion(prompt)
      if (messageType) {
        request.setMessageType(messageType)
      }
      if (isAgentChat) {
        request.setAgentChat(isAgentChat)
      }
      if (fileUploads) {
        const fileUploadsList = []
        for (const file of fileUploads) {
          const fileBytes = await getBytesPayload(file)
          fileUploadsList.push(
            new FileUploadBody()
              .setFileName(file.name)
              .setMimeType(file.type)
              .setFile(fileBytes)
              .setCaption(file.name),
          )
        }
        request.setFileUploadsList(fileUploadsList)
      }
      if (playbookInputs) {
        request.setPlaybookInputs(playbookInputs)
      }
      try {
        const response = await client.sendChatMessage(request)
        const questionId = response.getQuestionId()
        const taskId = response.getTaskId()
        onSuccess(questionId, taskId)
      } catch (error) {
        onError(error)
      }
    },
    [client, isAgentChat],
  )

  return useCallback(
    async (props: UseSendChatMessageProps) => {
      const isAccountValid = isAgentChat
        ? true
        : await validateAccountBalance({
            answerType: QUESTIONNAIRE_ANSWER_TYPE.CONTEXT_AND_HISTORY,
            callback: () => sendChatMessage(props),
          })

      if (isAccountValid) {
        await sendChatMessage(props)
      }
    },
    [sendChatMessage, validateAccountBalance, isAgentChat],
  )
}

export type UseCheckTaskProps = {
  initialTaskId?: string
  onTaskCompleted: () => void
  onCustomStatus?: (customStatus: string) => void
  onError: (errorMessage: string) => void
}

const TASK_COMPLETED_PROGRESS = 1
const ANSWER_POLLING_INTERVAL = 2000
const MAX_POLLING_ATTEMPTS = 50000

export const useCheckTask = (
  props: UseCheckTaskProps,
): ((taskId: string) => void) => {
  const [taskIdToCheck, setTaskIdToCheck] = useState<string | undefined>(
    props.initialTaskId,
  )
  const client = useAuthorizedGrpcClient(TaskManagerPromiseClient)
  const countRef = useRef(0)

  useEffect(() => {
    countRef.current = 0
  }, [taskIdToCheck])

  useAsyncInterval(async () => {
    if (!taskIdToCheck) {
      return
    }

    const taskCompleted = async () => {
      const request = new TaskID().setTaskId(taskIdToCheck)
      const response = await client.getProgress(request)
      const progress = response.getProgress()
      return {
        taskDone: progress === TASK_COMPLETED_PROGRESS,
        customStatus: response.getCustomStatus(),
      }
    }

    const processProgress = async () => {
      const cleanup = () => {
        setTaskIdToCheck(undefined)
      }
      let isTaskCompleted = false
      try {
        const { taskDone, customStatus } = await taskCompleted()
        isTaskCompleted = taskDone
        props.onCustomStatus?.(customStatus)
        if (isTaskCompleted) {
          cleanup()
          props.onTaskCompleted()
        }
      } catch (e: unknown) {
        props.onError('An error occurred while waiting for the answer')
        log.error('Error polling for task progress', e)
      } finally {
        countRef.current++
        if (countRef.current === MAX_POLLING_ATTEMPTS) {
          cleanup()
          if (!isTaskCompleted) {
            const errorMessage = 'Waited too long but no answer was received'
            props.onError(errorMessage)
            log.error(errorMessage)
          }
        }
      }
    }

    await processProgress()
  }, ANSWER_POLLING_INTERVAL)

  return setTaskIdToCheck
}

export const useGetRecentMessages = (
  chatId: string,
): ((since: number) => Promise<{
  messages: ChatMessage[]
  chatTitle: string
  intermediateStatus: GetIntermediateStatusResponse | undefined
}>) => {
  const client = useAuthorizedGrpcClient(ChatServicePromiseClient)

  return useCallback(
    async (since: number) => {
      const request = new GetChatMessagesSinceRequest()
        .setSince(since)
        .setChatId(chatId)
      const response = await client.getChatMessagesSince(request)
      return {
        messages: response.getMessagesList(),
        chatTitle: response.getChatTitle(),
        intermediateStatus: response.getIntermediateStatus(),
      }
    },
    [client, chatId],
  )
}

export const useDeleteChat = (): ((chatId: string) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ChatServicePromiseClient)

  return useCallback(
    async (chatId: string) => {
      const request = new DeleteChatRequest().setChatId(chatId)
      await client.deleteChat(request)
    },
    [client],
  )
}

export const useGetOrCreateAppCopilotChat = (): ((
  referringContext: ReferringContext,
  referringContextMetadata?: ReferringContextMetadata,
  title?: string,
) => Promise<UseCreateChatResponse>) => {
  const client = useAuthorizedGrpcClient(ChatServicePromiseClient)

  return useCallback(
    async (
      referringContext: ReferringContext,
      referringContextMetadata?: ReferringContextMetadata,
      title?: string,
    ) => {
      const refContext = new ReferringContextProto()
        .setObjectType(referringContext.objectType)
        .setObjectId(referringContext.objectId || '')
      const request = new GetOrCreateAppCopilotChatRequest()
        .setReferringContext(refContext)
        .setTitle(title || '')
      const metadata = toAppCopilotMetadataProto(referringContextMetadata)
      request.setReferringContextMetadata(metadata)
      const response = await client.getOrCreateAppCopilotChat(request)
      const chat = response.getChat()
      if (!chat) {
        throw new Error('Failed to fetch or create app copilot chat')
      }
      return { chat }
    },
    [client],
  )
}

export const useListAppCopilotChats = (
  referringContext: ReferringContext,
): GrpcResponse<ListAppCopilotChatsResponse> => {
  const refContext = new ReferringContextProto()
    .setObjectType(referringContext.objectType)
    .setObjectId(referringContext.objectId || '')
  const request = new ListAppCopilotChatsRequest().setReferringContext(
    refContext,
  )
  const { response } = useSwrGrpc(
    ChatServicePromiseClient.prototype.listAppCopilotChats,
    request,
  )
  return NewGrpcResponse(response)
}

export const useCreateAppCopilotChat = (): ((
  referringContext: ReferringContext,
  title: string,
  referringContextMetadata?: ReferringContextMetadata,
) => Promise<{ chat: Chat }>) => {
  const client = useAuthorizedGrpcClient(ChatServicePromiseClient)

  return useCallback(
    async (
      referringContext: ReferringContext,
      title: string,
      referringContextMetadata?: ReferringContextMetadata,
    ) => {
      const refContext = new ReferringContextProto()
        .setObjectType(referringContext.objectType)
        .setObjectId(referringContext.objectId || '')
      const request = new CreateAppCopilotChatRequest()
        .setReferringContext(refContext)
        .setTitle(title)
      const metadata = toAppCopilotMetadataProto(referringContextMetadata)
      request.setReferringContextMetadata(metadata)
      const response: CreateAppCopilotChatResponse =
        await client.createAppCopilotChat(request)
      const chat = response.getChat()
      if (!chat) {
        throw new Error('Failed to create app copilot chat')
      }
      return { chat }
    },
    [client],
  )
}

export const useChatPermalink = (
  chatId: string,
  artifactId?: string,
): string => {
  const params = useParams()

  return useMemo(
    () =>
      generatePermalink({
        pageContext: params.pageContext ?? '',
        modelType: MODEL_TYPE.CHAT,
        modelId: chatId,
        isInternalLink: false,
        includeSearchParams: artifactId ? true : undefined,
        search: artifactId
          ? queryString.parse(`?artifact=${artifactId}`, {
              arrayFormat: 'bracket',
            })
          : undefined,
      }),
    [chatId, params.pageContext, artifactId],
  )
}

export const useNavigateToChat = (): ((
  chatId: string,
  messageId?: string,
) => void) => {
  const navigate = useNavigate()
  const params = useParams()

  return useCallback(
    (chatId: string, messageId?: string) => {
      const basePath = `/${params.pageContext}/${TrusteroIntelligenceAbsoluteRoutes.SHOW}/${chatId}`
      const url = messageId ? `${basePath}?messageId=${messageId}` : basePath
      navigate(url)
    },
    [navigate, params.pageContext],
  )
}

export const useUpdateChat = (): ((
  chat: Chat,
  playbookInputs?: PlaybookInputs,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ChatServicePromiseClient)

  return useCallback(
    async (chat: Chat, playbookInputs?: PlaybookInputs) => {
      const request = new UpdateChatRequest()
        .setChatId(chat.getId())
        .setIsPrivate(chat.getIsPrivate())
        .setName(chat.getName())
        .setModel(chat.getModel())
        .setLeadPrompt(chat.getLeadPrompt())
        .setInitiatePrompt(chat.getInitiatePrompt())
      if (playbookInputs) {
        request.setPlaybookInputs(playbookInputs)
      }
      await client.updateChat(request)
    },
    [client],
  )
}

export type UseEditChatContextProps = {
  chatContextId?: string
  setOriginalContext?: (context: ChatContext) => void
}

export const useEditChatContext = ({
  chatContextId,
  setOriginalContext,
}: UseEditChatContextProps): {
  setContext: (chatContext: ChatContext) => Promise<void>
  isUpdatingContext: boolean
  errorUpdatingContext: string | null
} => {
  const mutate = useUpdateChatContext()
  const [isUpdatingContext, setIsUpdatingContext] = useState(false)
  const [errorUpdatingContext, setErrorUpdatingContext] = useState<
    string | null
  >(null)

  const setContext = useCallback(
    async (newContext: ChatContext) => {
      try {
        if (chatContextId) {
          setIsUpdatingContext(true)
          await mutate(chatContextId, newContext)
        }
        setOriginalContext?.(newContext)
      } catch (error) {
        log.error('Failed updating chat context', error)
        setErrorUpdatingContext?.(
          error instanceof Error
            ? error.message
            : 'Error updating chat context',
        )
      } finally {
        setIsUpdatingContext?.(false)
      }
    },
    [setOriginalContext, mutate, chatContextId],
  )

  return useMemo(
    () => ({
      setContext,
      isUpdatingContext,
      errorUpdatingContext,
    }),
    [setContext, isUpdatingContext, errorUpdatingContext],
  )
}

export const useReactToAnswer = (): ((
  answerId: string,
  reaction: AnswerReactionType,
  feedback: string,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ChatServicePromiseClient)

  return useCallback(
    async (
      answerId: string,
      reaction: AnswerReactionType,
      feedback: string,
    ) => {
      const request = new ReactToAnswerRequest()
      request.setAnswerId(answerId)
      request.setReaction(reaction)
      request.setFeedback(feedback)
      await client.reactToAnswer(request)
    },
    [client],
  )
}

export const useDeleteReactionToAnswer = (): ((
  answerId: string,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ChatServicePromiseClient)

  return useCallback(
    async (answerId: string) => {
      const request = new DeleteReactionToAnswerRequest()
      request.setAnswerId(answerId)
      await client.deleteReactionToAnswer(request)
    },
    [client],
  )
}
